import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import LandingPage from "@/pages/LandingPage";
import SuperAdminHub from "@/pages/SuperAdminHub";
import ClientDashboard from "@/pages/ClientDashboard";
import PerformerSite from "@/pages/PerformerSite";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import OrderPage from "@/pages/OrderPage";
import api from "@/lib/api";

// Check if current hostname is a custom domain (not the main Boxit app)
const MAIN_HOSTS = [
  'localhost',
  '127.0.0.1',
  'boxit.website',
  'www.boxit.website',
];

function isCustomDomain() {
  const host = window.location.hostname.toLowerCase();
  if (MAIN_HOSTS.some(h => host === h || host.endsWith('.emergentagent.com'))) return false;
  return true;
}

// Wrapper for custom domain sites — renders performer site directly
function CustomDomainSite() {
  const [siteData, setSiteData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const domain = window.location.hostname.toLowerCase();
    api.get('/public/domain-lookup', { params: { domain } })
      .then(res => setSiteData(res.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null; // Falls through to normal routing
  if (!siteData) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
    </div>
  );
  return <PerformerSite customSiteData={siteData} />;
}

// Wrapper component to handle navigation
function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('boxit_token');
    const storedUser = localStorage.getItem('boxit_user');
    if (token && storedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('boxit_token', token);
    localStorage.setItem('boxit_user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    
    // Redirect based on role
    if (userData.role === 'super_admin') {
      navigate('/hub');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('boxit_token');
    localStorage.removeItem('boxit_user');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Routes>
      {/* Public Marketing Site */}
      <Route 
        path="/" 
        element={<LandingPage onLoginClick={handleLoginClick} />} 
      />
      
      {/* Login Page */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? 
            <Navigate to={isSuperAdmin ? "/hub" : "/dashboard"} replace /> : 
            <LoginPage onLogin={handleLogin} />
        } 
      />
      
      {/* Super Admin Hub - billing, all sites, user management */}
      <Route 
        path="/hub/*" 
        element={
          isAuthenticated && isSuperAdmin ? 
            <SuperAdminHub user={user} onLogout={handleLogout} /> : 
            <Navigate to="/login" replace />
        } 
      />
      
      {/* Legacy Admin Dashboard (redirect super admins to hub) */}
      <Route 
        path="/admin/*" 
        element={
          isAuthenticated ? 
            (isSuperAdmin ? 
              <Navigate to="/hub" replace /> : 
              <Navigate to="/dashboard" replace />
            ) : 
            <Navigate to="/login" replace />
        } 
      />
      
      {/* Client Dashboard - edit their site */}
      <Route 
        path="/dashboard/*" 
        element={
          isAuthenticated ? 
            (isSuperAdmin ? 
              <Navigate to="/hub" replace /> :
              <ClientDashboard user={user} onLogout={handleLogout} />
            ) : 
            <Navigate to="/login" replace />
        } 
      />

      {/* Public Performer Sites */}
      <Route 
        path="/sites/:slug" 
        element={<PerformerSite />} 
      />
      <Route path="/sites/:slug/booking-success" element={<BookingSuccessPage />} />

      {/* Order / Onboarding */}
      <Route path="/order" element={<OrderPage />} />
      <Route path="/order/success" element={<OrderPage />} />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // If accessing via a custom domain, render the performer site directly
  if (isCustomDomain()) {
    return (
      <div className="min-h-screen">
        <BrowserRouter>
          <CustomDomainSite />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
